import {
  DOCTOR_NOTE_CONTENT_MAX,
  DOCTOR_NOTE_DEFAULT_THERAPIES,
  DOCTOR_NOTE_INSTRUCTIONS_MAX,
  DOCTOR_NOTE_KINDS,
  DOCTOR_NOTE_THERAPIES,
  type DoctorNoteKind,
  type DoctorNoteOptions,
  type DoctorNoteTherapy,
} from "@rosc/contracts";
import { Button } from "@rosc/ui/v32/button";
import { Checkbox } from "@rosc/ui/v32/checkbox";
import { Field, FieldLabel } from "@rosc/ui/v32/field";
import type { JSX } from "react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReferralSheet } from "@/components/ui/referral/sheet";
import {
  FIELD_HINT,
  OPTION_CARD,
  RADIO,
} from "@/components/ui/referral/styles";
import { cn } from "@/lib/utils";
import { DictatedField } from "./DictatedField";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (options: DoctorNoteOptions) => void;
}

// Create → Doctor's note (clinical lead, 05/11/2026): one of the clinical
// lead's notes (sick note, compression stockings, therapy coverage,
// orthotics), filled from this visit, or a custom note the clinician
// dictates or types with instructions on how to write it.
export const DoctorNoteDialog = ({
  open,
  onOpenChange,
  onGenerate,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [kind, setKind] = useState<DoctorNoteKind>("sick-note");
  const [therapies, setTherapies] = useState<DoctorNoteTherapy[]>([
    ...DOCTOR_NOTE_DEFAULT_THERAPIES,
  ]);
  const [content, setContent] = useState("");
  const [instructions, setInstructions] = useState("");
  const [touched, setTouched] = useState(false);
  const ids = {
    kind: useId(),
    content: useId(),
    contentHint: useId(),
    instructions: useId(),
    therapies: useId(),
  };

  // A fresh form each time it opens.
  useEffect(() => {
    if (!open) return;
    setKind("sick-note");
    setTherapies([...DOCTOR_NOTE_DEFAULT_THERAPIES]);
    setContent("");
    setInstructions("");
    setTouched(false);
  }, [open]);

  const contentMissing = kind === "custom" && !content.trim();
  const therapiesMissing = kind === "therapy-coverage" && !therapies.length;
  const submit = (): void => {
    setTouched(true);
    if (contentMissing) {
      document.getElementById(ids.content)?.focus();
      return;
    }
    if (therapiesMissing) return;
    onOpenChange(false);
    onGenerate(
      kind === "custom"
        ? {
            kind,
            content: content.trim(),
            ...(instructions.trim()
              ? { instructions: instructions.trim() }
              : {}),
          }
        : kind === "therapy-coverage"
          ? {
              kind,
              // In the clinical lead's order, whatever order they were ticked.
              therapies: DOCTOR_NOTE_THERAPIES.filter((item) =>
                therapies.includes(item),
              ),
            }
          : { kind },
    );
  };

  return (
    <ReferralSheet
      description={t("doctorNote.dialog.description")}
      footer={
        <>
          <Button
            className="max-sm:h-12"
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            className="max-sm:h-12"
            data-testid="doctor-note-generate"
            type="submit"
            variant="primary"
          >
            {t("doctorNote.dialog.generate")}
          </Button>
        </>
      }
      open={open}
      testId="doctor-note-dialog"
      title={t("doctorNote.dialog.title")}
      onOpenChange={onOpenChange}
      onSubmit={submit}
    >
      <fieldset className="space-y-2">
        <legend className="mb-2 block font-v3 text-[13px] font-semibold leading-[18px] text-v3-text-2">
          {t("doctorNote.dialog.template")}
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {DOCTOR_NOTE_KINDS.map((item) => (
            <label
              key={item}
              className={cn(OPTION_CARD, item === "custom" && "sm:col-span-2")}
            >
              <input
                checked={kind === item}
                className={RADIO}
                data-testid={`doctor-note-kind-${item}`}
                name={ids.kind}
                type="radio"
                value={item}
                onChange={() => {
                  setKind(item);
                  setTouched(false);
                }}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold leading-5 text-v3-text">
                  {t(`doctorNote.kinds.${item}.name`)}
                </span>
                <span className="mt-0.5 block text-[12.5px] leading-[18px] text-v3-text-2">
                  {t(`doctorNote.kinds.${item}.description`)}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {kind === "therapy-coverage" && (
        <fieldset className="space-y-1" data-testid="doctor-note-therapies">
          <legend className="mb-1 block font-v3 text-[13px] font-semibold leading-[18px] text-v3-text-2">
            {t("doctorNote.dialog.therapies")}
          </legend>
          <div className="grid gap-x-4 sm:grid-cols-2">
            {DOCTOR_NOTE_THERAPIES.map((item) => (
              <Checkbox
                key={item}
                checked={therapies.includes(item)}
                label={t(`doctorNote.therapies.${item}`)}
                testId={`doctor-note-therapy-${item}`}
                onCheckedChange={(checked) => {
                  setTherapies((current) =>
                    checked
                      ? [...current, item]
                      : current.filter((value) => value !== item),
                  );
                }}
              />
            ))}
          </div>
          {touched && therapiesMissing && (
            <p className={cn(FIELD_HINT, "text-v3-rec-text")} role="alert">
              {t("doctorNote.dialog.therapiesRequired")}
            </p>
          )}
        </fieldset>
      )}

      {kind === "custom" ? (
        <>
          <div className="min-w-0 space-y-1.5 font-v3">
            <FieldLabel htmlFor={ids.content}>
              {t("doctorNote.dialog.content")}
            </FieldLabel>
            <DictatedField
              describedBy={ids.contentHint}
              id={ids.content}
              invalid={touched && contentMissing}
              maxLength={DOCTOR_NOTE_CONTENT_MAX}
              micLabel={{
                start: t("doctorNote.dialog.dictateContent"),
                stop: t("dictation.stop"),
              }}
              placeholder={t("doctorNote.dialog.contentPlaceholder")}
              testId="doctor-note-content"
              value={content}
              onChange={setContent}
            />
            <p
              className={cn(
                FIELD_HINT,
                touched && contentMissing && "text-v3-rec-text",
              )}
              id={ids.contentHint}
            >
              {touched && contentMissing
                ? t("doctorNote.dialog.contentRequired")
                : t("doctorNote.dialog.contentHint")}
            </p>
          </div>
          <Field
            hint={t("doctorNote.dialog.instructionsHint")}
            htmlFor={ids.instructions}
            label={
              <>
                {t("doctorNote.dialog.instructions")}{" "}
                <span className="font-normal text-v3-text-3">
                  {t("referral.dialog.optional")}
                </span>
              </>
            }
          >
            <DictatedField
              id={ids.instructions}
              maxLength={DOCTOR_NOTE_INSTRUCTIONS_MAX}
              micLabel={{
                start: t("doctorNote.dialog.dictateInstructions"),
                stop: t("dictation.stop"),
              }}
              minRows={2}
              placeholder={t("doctorNote.dialog.instructionsPlaceholder")}
              testId="doctor-note-instructions"
              value={instructions}
              onChange={setInstructions}
            />
          </Field>
        </>
      ) : (
        <p
          className="rounded-xl bg-v3-surface-2 px-3 py-3 text-[13px] leading-5 text-v3-text-2"
          data-testid="doctor-note-fill-hint"
        >
          {t("doctorNote.dialog.fillHint")}
        </p>
      )}
    </ReferralSheet>
  );
};
